import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  apiErrorMessage,
  getAdminFriendshipSummary,
  getAdminUserContent,
  getAdminUserProfile,
  listAdminFollowRequests,
  listAdminFollows,
  loadAdminUsers,
  normalizeResponseData,
  updateAdminUserProfile
} from "../../core/api/adminApi";
import { useAuth } from "../../core/auth/AuthContext";
import type {
  AdminFollowRequestsListPayload,
  AdminFollowsListPayload,
  AdminFriendshipSummaryPayload,
  AdminUserContentItem,
  AdminUserContentPayload,
  AdminUserProfile,
  MigrationUserListItem
} from "../../shared/types/admin";
import { resolveAvatarSrc } from "../../shared/ui/avatar";

type ActionState = "info" | "success" | "error";
type UserDetailTab = "overview" | "content";

const TAB_ORDER: UserDetailTab[] = ["overview", "content"];

function normalizeTab(value: string | null): UserDetailTab {
  if (TAB_ORDER.includes(value as UserDetailTab)) {
    return value as UserDetailTab;
  }
  return "overview";
}

function resolveUserId(user: MigrationUserListItem): string {
  return user.userId || user.uid || "";
}

function formatDate(value?: string): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("de-DE");
}

function maskToken(value?: string): string {
  if (!value) return "-";
  if (value.length <= 20) return value;
  return `${value.slice(0, 12)}...${value.slice(-8)}`;
}

function parseCsvValues(value: string): string[] {
  return value
    .split(",")
    .map((entry) => entry.trim())
    .filter((entry) => !!entry)
    .map((entry) => entry.toLowerCase())
    .filter((entry, index, arr) => arr.indexOf(entry) === index);
}

function Badge({ label, ok }: { label: string; ok?: boolean }) {
  return <span className={`badge ${ok ? "ok" : ""}`}>{label}</span>;
}

function ItemRow({ item }: { item: AdminUserContentItem }) {
  return (
    <div className="item-row">
      <div className="item-title">{item.name || "(ohne Name)"}</div>
      <div className="item-meta">{[item.address, item.placeId].filter(Boolean).join(" • ") || "-"}</div>
      {item.userComment ? <div className="item-comment">"{item.userComment}"</div> : null}
      <div className="item-tags">
        {(item.types || []).map((type) => (
          <span key={type} className="item-tag">
            {type}
          </span>
        ))}
      </div>
    </div>
  );
}

export function UserDetailPage() {
  const { userId: routeUserId = "" } = useParams();
  const decodedUserId = decodeURIComponent(routeUserId);
  const { state } = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useAuth();

  const initialUser = (state as { user?: MigrationUserListItem } | undefined)?.user;
  const [activeTab, setActiveTab] = useState<UserDetailTab>(() => normalizeTab(searchParams.get("tab")));
  const [user, setUser] = useState<MigrationUserListItem | undefined>(initialUser);
  const [items, setItems] = useState<AdminUserContentPayload | undefined>();
  const [size, setSize] = useState(25);
  const [wcoPage, setWcoPage] = useState(0);
  const [bucketPage, setBucketPage] = useState(0);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profile, setProfile] = useState<AdminUserProfile | undefined>();
  const [friendshipSummary, setFriendshipSummary] = useState<AdminFriendshipSummaryPayload | undefined>();
  const [friendshipFollows, setFriendshipFollows] = useState<AdminFollowsListPayload | undefined>();
  const [friendshipRequests, setFriendshipRequests] = useState<AdminFollowRequestsListPayload | undefined>();
  const [profileForm, setProfileForm] = useState({
    name: "",
    avatar: "",
    bio: "",
    city: "",
    email: "",
    website: "",
    wallpaper: "",
    push: false,
    sticker: false,
    autoAcceptFollower: false,
    flagsText: "",
    rolesText: ""
  });
  const [status, setStatus] = useState<{ type: ActionState; message: string } | null>(null);
  const [error, setError] = useState("");

  const changeTab = (tab: UserDetailTab) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    if (tab === "overview") {
      next.delete("tab");
    } else {
      next.set("tab", tab);
    }
    setSearchParams(next, { replace: true });
  };

  const loadUserSummary = async () => {
    try {
      const response = await loadAdminUsers(token, {
        query: decodedUserId,
        limit: 200,
        sort: "updated"
      });
      const payload = normalizeResponseData(response);
      const list = payload?.users || [];
      const found = list.find((it) => resolveUserId(it) === decodedUserId);
      if (found) {
        setUser(found);
      }
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  };

  const setProfileDraft = (value: AdminUserProfile | undefined) => {
    setProfile(value);
    setProfileForm({
      name: value?.name || "",
      avatar: value?.avatar || "",
      bio: value?.bio || "",
      city: value?.city || "",
      email: value?.email || "",
      website: value?.website || "",
      wallpaper: value?.wallpaper || "",
      push: !!value?.push,
      sticker: !!value?.sticker,
      autoAcceptFollower: !!value?.autoAcceptFollower,
      flagsText: (value?.flags || []).join(", "),
      rolesText: (value?.roles || []).join(", ")
    });
  };

  const loadProfile = async () => {
    setProfileLoading(true);
    try {
      const response = await getAdminUserProfile(token, decodedUserId);
      setProfileDraft(normalizeResponseData(response));
      return response;
    } finally {
      setProfileLoading(false);
    }
  };

  const loadItems = async (nextWcoPage = wcoPage, nextBucketPage = bucketPage, nextSize = size) => {
    const response = await getAdminUserContent(token, decodedUserId, nextWcoPage, nextBucketPage, nextSize);
    const payload = normalizeResponseData(response);
    setItems(payload);
    return response;
  };

  const loadFriendships = async () => {
    try {
      const [summaryResponse, followsResponse, requestsResponse] = await Promise.all([
        getAdminFriendshipSummary(token, decodedUserId),
        listAdminFollows(token, { userId: decodedUserId, page: 0, size: 5 }),
        listAdminFollowRequests(token, { userId: decodedUserId, page: 0, size: 5, status: "PENDING" })
      ]);
      setFriendshipSummary(normalizeResponseData(summaryResponse));
      setFriendshipFollows(normalizeResponseData(followsResponse));
      setFriendshipRequests(normalizeResponseData(requestsResponse));
    } catch {
      setFriendshipSummary(undefined);
      setFriendshipFollows(undefined);
      setFriendshipRequests(undefined);
    }
  };

  useEffect(() => {
    const next = normalizeTab(searchParams.get("tab"));
    setActiveTab(next);
  }, [searchParams]);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      setError("");
      try {
        if (!initialUser) {
          await loadUserSummary();
        }
        await Promise.all([loadItems(0, 0, size), loadProfile(), loadFriendships()]);
      } catch (e) {
        if (!mounted) return;
        setError(apiErrorMessage(e));
      }
    };
    void run();
    return () => {
      mounted = false;
    };
  }, [decodedUserId]);

  const sortedDbWco = useMemo(
    () => [...(items?.wecommendations || [])].sort((a, b) => (a.placeId || "").localeCompare(b.placeId || "")),
    [items?.wecommendations]
  );
  const sortedDbBucket = useMemo(
    () => [...(items?.bucketlist || [])].sort((a, b) => (a.placeId || "").localeCompare(b.placeId || "")),
    [items?.bucketlist]
  );

  const saveProfile = async () => {
    setProfileSaving(true);
    setStatus({ type: "info", message: "Profil speichern..." });
    setError("");
    try {
      const response = await updateAdminUserProfile(token, decodedUserId, {
        name: profileForm.name,
        avatar: profileForm.avatar,
        bio: profileForm.bio,
        city: profileForm.city,
        email: profileForm.email,
        website: profileForm.website,
        wallpaper: profileForm.wallpaper,
        push: profileForm.push,
        sticker: profileForm.sticker,
        autoAcceptFollower: profileForm.autoAcceptFollower,
        flags: parseCsvValues(profileForm.flagsText),
        roles: parseCsvValues(profileForm.rolesText)
      });
      const data = normalizeResponseData(response);
      setProfileDraft(data);
      await Promise.all([loadUserSummary(), loadFriendships()]);
      setStatus({ type: "success", message: "Profil erfolgreich gespeichert." });
    } catch (e) {
      const message = apiErrorMessage(e);
      setError(message);
      setStatus({ type: "error", message: `Profil speichern fehlgeschlagen: ${message}` });
    } finally {
      setProfileSaving(false);
    }
  };

  const totalWco = items?.wecommendationsTotal || 0;
  const totalBucket = items?.bucketlistTotal || 0;
  const wcoPages = Math.max(1, Math.ceil(totalWco / size));
  const bucketPages = Math.max(1, Math.ceil(totalBucket / size));
  const headerAvatar = resolveAvatarSrc(decodedUserId, profile?.avatar || user?.avatar);
  const headerName = profile?.name || user?.name || "(ohne Name)";
  const headerEmail = profile?.email || user?.email || "-";
  const headerCity = profile?.city || user?.city || "-";

  return (
    <section>
      <div className="view-headline">
        <h1>User Detail</h1>
        <p className="muted">Profil, Beziehungen und produktiver Content für den ausgewählten User.</p>
      </div>

      <div className="card">
        <div className="user-header">
          {headerAvatar ? <img src={headerAvatar} className="avatar avatar-lg" alt="avatar" /> : <div className="avatar avatar-lg placeholder" />}
          <div>
            <div className="user-title">{headerName}</div>
            <div className="muted">{headerEmail}</div>
            <div className="muted">{decodedUserId}</div>
          </div>
          <div className="user-header-actions">
            <button type="button" onClick={() => navigate("/users")}>
              Zurück zur Liste
            </button>
          </div>
        </div>

        <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 8 }}>
          <Badge label="User" ok={!!user?.dbUserExists} />
          <Badge label={`WCO ${user?.dbWcoCount ?? 0}`} ok={(user?.dbWcoCount ?? 0) > 0} />
          <Badge label={`Bucket ${user?.dbBucketCount ?? 0}`} ok={(user?.dbBucketCount ?? 0) > 0} />
          <Badge label={`Flags ${user?.dbFlagsCount ?? 0}`} ok={(user?.dbFlagsCount ?? 0) > 0} />
        </div>

        <div className="muted" style={{ marginTop: 8 }}>
          {headerCity} • Updated: {formatDate(user?.updatedAt)} • Last Activity: {formatDate(user?.activeAt || user?.updatedAt)}
        </div>
      </div>

      <div className="tab-row" style={{ marginTop: 16 }}>
        <button type="button" className={activeTab === "overview" ? "primary" : ""} onClick={() => changeTab("overview")}>
          Übersicht
        </button>
        <button type="button" className={activeTab === "content" ? "primary" : ""} onClick={() => changeTab("content")}>
          Content
        </button>
      </div>

      {error ? <div className="error-box">{error}</div> : null}
      {status ? <div className={`action-status ${status.type}`}>{status.message}</div> : null}

      {activeTab === "overview" ? (
        <div className="stack-gap" style={{ marginTop: 16 }}>
          <div className="card">
            <div className="section-header">
              <label>Profil</label>
              <div className="muted">Live bearbeitbar</div>
            </div>
            <div className="profile-panel">
              <div className="profile-wallpaper">
                {profileForm.wallpaper || (profile?.wallpaper as string) ? (
                  <img src={(profileForm.wallpaper || profile?.wallpaper) as string} alt="wallpaper" />
                ) : null}
              </div>
              <div className="profile-info">
                <div className="profile-edit-grid">
                  <div>
                    <label>Name</label>
                    <input value={profileForm.name} onChange={(event) => setProfileForm((prev) => ({ ...prev, name: event.target.value }))} />
                  </div>
                  <div>
                    <label>E-Mail</label>
                    <input value={profileForm.email} onChange={(event) => setProfileForm((prev) => ({ ...prev, email: event.target.value }))} />
                  </div>
                  <div>
                    <label>City</label>
                    <input value={profileForm.city} onChange={(event) => setProfileForm((prev) => ({ ...prev, city: event.target.value }))} />
                  </div>
                  <div>
                    <label>Website</label>
                    <input value={profileForm.website} onChange={(event) => setProfileForm((prev) => ({ ...prev, website: event.target.value }))} />
                  </div>
                  <div>
                    <label>Avatar URL</label>
                    <input value={profileForm.avatar} onChange={(event) => setProfileForm((prev) => ({ ...prev, avatar: event.target.value }))} />
                  </div>
                  <div>
                    <label>Wallpaper URL</label>
                    <input value={profileForm.wallpaper} onChange={(event) => setProfileForm((prev) => ({ ...prev, wallpaper: event.target.value }))} />
                  </div>
                  <div className="profile-edit-full">
                    <label>Bio</label>
                    <textarea rows={3} value={profileForm.bio} onChange={(event) => setProfileForm((prev) => ({ ...prev, bio: event.target.value }))} />
                  </div>
                  <div>
                    <label>Flags (kommagetrennt)</label>
                    <input
                      value={profileForm.flagsText}
                      onChange={(event) => setProfileForm((prev) => ({ ...prev, flagsText: event.target.value }))}
                      placeholder="expert"
                    />
                  </div>
                  <div>
                    <label>Roles (kommagetrennt)</label>
                    <input
                      value={profileForm.rolesText}
                      onChange={(event) => setProfileForm((prev) => ({ ...prev, rolesText: event.target.value }))}
                      placeholder="admin"
                    />
                  </div>
                  <div className="profile-toggle-row">
                    <label className="checkbox-inline">
                      <input
                        type="checkbox"
                        checked={profileForm.push}
                        onChange={(event) => setProfileForm((prev) => ({ ...prev, push: event.target.checked }))}
                      />
                      Push
                    </label>
                    <label className="checkbox-inline">
                      <input
                        type="checkbox"
                        checked={profileForm.sticker}
                        onChange={(event) => setProfileForm((prev) => ({ ...prev, sticker: event.target.checked }))}
                      />
                      Sticker
                    </label>
                    <label className="checkbox-inline">
                      <input
                        type="checkbox"
                        checked={profileForm.autoAcceptFollower}
                        onChange={(event) => setProfileForm((prev) => ({ ...prev, autoAcceptFollower: event.target.checked }))}
                      />
                      Auto Accept
                    </label>
                    <button type="button" className="primary" onClick={() => void saveProfile()} disabled={profileSaving || profileLoading}>
                      {profileSaving ? "Speichere..." : "Profil speichern"}
                    </button>
                  </div>
                  <div className="muted">
                    Rollen: {(profile?.roles || []).join(", ") || "-"} · Flags: {(profile?.flags || []).join(", ") || "-"}
                  </div>
                  <div className="profile-edit-full">
                    <label>Push Tokens ({profile?.pushTokens?.length || 0})</label>
                    {(profile?.pushTokens || []).length === 0 ? (
                      <div className="muted">Keine registrierten Tokens.</div>
                    ) : (
                      <div className="token-list">
                        {(profile?.pushTokens || []).map((item) => (
                          <div className="token-list-item" key={item.id || item.token}>
                            <div>
                              <strong>{item.platform || "-"}</strong>
                              {" · "}
                              {item.appVersion || "-"}
                              {" ("}
                              {item.appBuild || "-"}
                              {")"}
                              {" · "}
                              <code>{maskToken(item.token)}</code>
                            </div>
                            <div className="muted">Updated: {formatDate(item.lastModifiedDate)}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="section-header">
              <label>Friendships</label>
              <button type="button" className="tertiary" onClick={() => navigate(`/friendships?userId=${encodeURIComponent(decodedUserId)}`)}>
                In Friendships öffnen
              </button>
            </div>
            <div className="friendships-summary-grid compact">
              <div className="kpi-card">
                <div className="kpi-label">Followers</div>
                <div className="kpi-value">{friendshipSummary?.followersCount ?? 0}</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Following</div>
                <div className="kpi-value">{friendshipSummary?.followingCount ?? 0}</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Pending In</div>
                <div className="kpi-value">{friendshipSummary?.pendingIncomingCount ?? 0}</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-label">Pending Out</div>
                <div className="kpi-value">{friendshipSummary?.pendingOutgoingCount ?? 0}</div>
              </div>
            </div>
            <div className="friendships-preview-grid">
              <div>
                <div className="muted">Neueste Follows</div>
                <div className="friendships-preview-list">
                  {(friendshipFollows?.items || []).slice(0, 5).map((item) => (
                    <div key={item.id} className="friendship-mini-row">
                      <button
                        type="button"
                        className="link-button"
                        onClick={() => item.follower?.userId && navigate(`/users/${encodeURIComponent(item.follower.userId)}`)}
                      >
                        {item.follower?.name || item.follower?.userId || "-"}
                      </button>
                      <span className="muted">→</span>
                      <button
                        type="button"
                        className="link-button"
                        onClick={() => item.followed?.userId && navigate(`/users/${encodeURIComponent(item.followed.userId)}`)}
                      >
                        {item.followed?.name || item.followed?.userId || "-"}
                      </button>
                    </div>
                  ))}
                  {(friendshipFollows?.items || []).length === 0 ? <div className="muted">Keine Follows.</div> : null}
                </div>
              </div>
              <div>
                <div className="muted">Pending Requests</div>
                <div className="friendships-preview-list">
                  {(friendshipRequests?.items || []).slice(0, 5).map((item) => (
                    <div key={item.id} className="friendship-mini-row">
                      <button
                        type="button"
                        className="link-button"
                        onClick={() => item.requester?.userId && navigate(`/users/${encodeURIComponent(item.requester.userId)}`)}
                      >
                        {item.requester?.name || item.requester?.userId || "-"}
                      </button>
                      <span className="muted">→</span>
                      <button
                        type="button"
                        className="link-button"
                        onClick={() => item.requested?.userId && navigate(`/users/${encodeURIComponent(item.requested.userId)}`)}
                      >
                        {item.requested?.name || item.requested?.userId || "-"}
                      </button>
                    </div>
                  ))}
                  {(friendshipRequests?.items || []).length === 0 ? <div className="muted">Keine pending Requests.</div> : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {activeTab === "content" ? (
        <div className="stack-gap" style={{ marginTop: 16 }}>
          <div className="card">
            <div className="section-header">
              <label>Wecommendations</label>
              <div className="pager">
                <span>Size</span>
                <select
                  value={size}
                  onChange={async (event) => {
                    const nextSize = Number(event.target.value);
                    setSize(nextSize);
                    setWcoPage(0);
                    setBucketPage(0);
                    await loadItems(0, 0, nextSize);
                  }}
                >
                  {[10, 25, 50, 100].map((entry) => (
                    <option key={entry} value={entry}>
                      {entry}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  disabled={wcoPage <= 0}
                  onClick={async () => {
                    const next = Math.max(0, wcoPage - 1);
                    setWcoPage(next);
                    await loadItems(next, bucketPage, size);
                  }}
                >
                  ‹
                </button>
                <span className="pager-info">
                  Seite {wcoPage + 1}/{wcoPages} · {items?.wecommendationsTotal ?? 0} produktive Einträge
                </span>
                <button
                  type="button"
                  disabled={(wcoPage + 1) * size >= totalWco}
                  onClick={async () => {
                    const next = wcoPage + 1;
                    setWcoPage(next);
                    await loadItems(next, bucketPage, size);
                  }}
                >
                  ›
                </button>
              </div>
            </div>

            <div className="item-list">
              {sortedDbWco.length === 0 ? <div className="muted">Keine Einträge</div> : null}
              {sortedDbWco.map((item) => (
                <ItemRow key={`${item.placeId}-${item.name}-db`} item={item} />
              ))}
            </div>
          </div>

          <div className="card">
            <div className="section-header">
              <label>Bucketlist</label>
              <div className="pager">
                <button
                  type="button"
                  disabled={bucketPage <= 0}
                  onClick={async () => {
                    const next = Math.max(0, bucketPage - 1);
                    setBucketPage(next);
                    await loadItems(wcoPage, next, size);
                  }}
                >
                  ‹
                </button>
                <span className="pager-info">
                  Seite {bucketPage + 1}/{bucketPages} · {items?.bucketlistTotal ?? 0} produktive Einträge
                </span>
                <button
                  type="button"
                  disabled={(bucketPage + 1) * size >= totalBucket}
                  onClick={async () => {
                    const next = bucketPage + 1;
                    setBucketPage(next);
                    await loadItems(wcoPage, next, size);
                  }}
                >
                  ›
                </button>
              </div>
            </div>

            <div className="item-list">
              {sortedDbBucket.length === 0 ? <div className="muted">Keine Einträge</div> : null}
              {sortedDbBucket.map((item) => (
                <ItemRow key={`${item.placeId}-${item.name}-db-b`} item={item} />
              ))}
            </div>
          </div>

          <div className="card">
            <div className="muted">
              Firebase-Compare und Import-Aktionen wurden aus dem täglichen Admin-Flow entfernt. Diese Ansicht zeigt nur produktive Postgres-Daten.
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
